import { useEffect, useState } from 'react';
import './PerformanceGraph.css';
import SectionLoader from '../../../../../_shoru_shared_library/loaders/SectionLoader';
import { CheckBox, CheckBoxOutlineBlank } from '@mui/icons-material';


export default function PerformanceGraph({graphs, averages, seasons}) {

  const [visible, setVisible] = useState({
    health: true,
    projects: true,
    roles: true,
    auditions: true,
    auditionees: true,
    auditionees_per_role: false,
    bookings_per_auditionee: false
  });
  const [hoveredPoint, setHoveredPoint] = useState(null);

  const toggleVisible = (c) => {
    setVisible((prev) => ({...prev, [c]: !prev[c]}))
  }

  const colours = {
    health: '#2ecc71',
    projects: '#f1c40f',
    roles: '#e74c3c',
    auditions: '#3498db',
    auditionees: '#1abc9c',
    auditionees_per_role: '#9b59b6',
    bookings_per_auditionee: '#e67e22'
  }

  const graphWidth = 800;
  const graphHeight = 400;
  const r = 5;
  const yDivisions = 5;
  const axisPadding = {
    left: 40,
    right: 40,
    top: 10,
    bottom: 50
  };

  const getPointX = (x) => axisPadding.left + (x / Math.max(maxX - 1, 1)) * (graphWidth - axisPadding.left - axisPadding.right);
  const getPointY = (y) => graphHeight - axisPadding.bottom - (y / maxY) * (graphHeight - axisPadding.top - axisPadding.bottom);

  const [maxY, setMaxY] = useState(null);
  const [maxX, setMaxX] = useState(null);
  const [points, setPoints] = useState(null);

  const tooltipWidth = 170;


  useEffect(() => {
    if(!graphs) return;

    const activeGraphs = structuredClone(graphs);

    Object.keys(activeGraphs).forEach((key) => {
      const graph = activeGraphs[key];
      Object.keys(graph.ys).forEach((subKey) => {
        if(!visible[subKey]) delete graph.ys[subKey]
      })
    });

    setMaxY(getRoundedHighest(activeGraphs));
    setMaxX(Object.keys(activeGraphs).length);
  }, [visible, graphs])


  return (
    <>
      {
        (graphs && maxY && maxX) 
        ? <div className='pg-container'>
            <p className='pg-label'>Statistics for the last 10 seasons</p>
            <div
              className="pg-graph-wrapper"
              style={{
                width: `${graphWidth}px`,
                height: `${graphHeight}px`
              }}
            >
              <svg width={graphWidth} height={graphHeight} viewBox={`0 0 ${graphWidth} ${graphHeight}`} xmlns="http://www.w3.org/2000/svg">
                {/* Y axis */}
                <line
                  x1={axisPadding.left}
                  y1={axisPadding.top}
                  x2={axisPadding.left}
                  y2={graphHeight - axisPadding.bottom}
                  stroke="#888"
                />

                {/* X axis */}
                <line
                  x1={axisPadding.left}
                  y1={graphHeight - axisPadding.bottom}
                  x2={graphWidth - axisPadding.right}
                  y2={graphHeight - axisPadding.bottom}
                  stroke="#888"
                />

                {seasons.map((season) => {
                  const x = getPointX(graphs[season.uid].x);
                  const y = graphHeight - axisPadding.bottom + 18;

                  const getLabelLines = (label) => {
                    const bracketIndex = label.indexOf(' (');

                    if (bracketIndex !== -1) {
                      return [
                        label.slice(0, bracketIndex),
                        label.slice(bracketIndex + 1)
                      ];
                    }

                    const yearMatch = label.match(/^(.*)\s(\d{4})$/);

                    if (yearMatch) {
                      return [yearMatch[1], yearMatch[2]];
                    }

                    const words = label.split(' ');
                    const midpoint = Math.ceil(words.length / 2);

                    return [
                      words.slice(0, midpoint).join(' '),
                      words.slice(midpoint).join(' ')
                    ];
                  };

                  const [line1, line2] = getLabelLines(season.name);

                  return (
                    <text
                      key={season.uid}
                      x={x}
                      y={y}
                      textAnchor="middle"
                      fontSize="12"
                      fill="#aaa"
                    >
                      <tspan x={x} dy="0">
                        {line1}
                      </tspan>

                      {line2 && (
                        <tspan x={x} dy="14">
                          {line2}
                        </tspan>
                      )}
                    </text>
                  );
                })}

                {Array.from({ length: yDivisions + 1 }, (_, i) => {
                  const value = Math.round((maxY / yDivisions) * i);

                  return (
                    <g key={i}>
                      <line
                        x1={axisPadding.left - 5}
                        x2={axisPadding.left}
                        y1={getPointY(value)}
                        y2={getPointY(value)}
                        stroke="#888"
                      />

                      <line
                        x1={axisPadding.left}
                        x2={graphWidth - axisPadding.right}
                        y1={getPointY(value)}
                        y2={getPointY(value)}
                        strokeWidth={1}
                        stroke="#353535"
                      />

                      <text
                        x={axisPadding.left - 10}
                        y={getPointY(value) + 4}
                        textAnchor="end"
                        fontSize="12"
                        fill="#aaa"
                      >
                        {value}
                      </text>
                    </g>
                  );
                })}

                {
                  // AVERAGES LINES
                  Array.from(Object.keys(visible).filter(x => visible[x]).map((graphId) =>
                      <>
                        <line
                          x1={axisPadding.left}
                          x2={graphWidth - axisPadding.right}
                          y1={getPointY(averages[graphId])}
                          y2={getPointY(averages[graphId])}
                          strokeWidth={1}
                          strokeDasharray="6 4"
                          stroke={`${colours[graphId]}55`}
                        />
                        <text
                          x={graphWidth - axisPadding.right + 10}
                          y={getPointY(averages[graphId]) + 3}
                          fontSize={11}
                          fontWeight={600}
                          fill={`${colours[graphId]}aa`}
                          textAnchor="right"
                        >
                          {averages[graphId].toFixed(2)}
                        </text>
                      </>
                    )
                  )
                }
                
                {
                  // LINES
                  Array.from(Object.keys(visible)).filter(x => visible[x]).map((graphId) => {
                    return seasons.map((season, i) => {
                      if(i === seasons.length - 1) return null;

                      const from = graphs[season.uid];
                      const to = graphs[seasons[i+1].uid];

                      const x1 = from.x;
                      const y1 = from.ys[graphId];
                      const x2 = to.x;
                      const y2 = to.ys[graphId];

                      if(y1 === 0 || y2 === 0) return null;

                      return (
                        <line
                          key={`${season.uid}-${graphId}`}
                          x1={getPointX(x1)} x2={getPointX(x2)}
                          y1={getPointY(y1)} y2={getPointY(y2)}
                          strokeWidth={2}
                          stroke={`${colours[graphId]}aa`}
                          style={{transition: '0.1s'}}
                        />
                      )
                    })
                  })
                }
                {
                  // CIRCLES
                  Array.from(Object.keys(visible)).filter(x => visible[x]).map((graphId) => {
                    return seasons.map((season) => {
                      const s = season.uid;
                      const x = graphs[s].x;
                      const y = graphs[s].ys[graphId];
                      const cx = getPointX(x);
                      const cy = getPointY(y);

                      if(y === 0) return null;

                      const isHovered = hoveredPoint?.seasonId === season.uid && hoveredPoint?.graphId === graphId;

                      return (
                        <g key={`${season.uid}-${graphId}`}>
                          <circle
                            cx={cx}
                            cy={cy}
                            r={isHovered ? (r+2) : r}
                            fill={isHovered ? '#fff' : colours[graphId]}
                            onMouseEnter={() => setHoveredPoint({
                              seasonId: season.uid,
                              graphId,
                              seasonName: season.name,
                              value: y,
                              cx, cy
                            })}
                            onMouseLeave={() => setHoveredPoint(null)}
                            style={{transition: '0.1s'}}
                          />
                        </g>
                      )
                    })
                  })
                }
              </svg>
              {hoveredPoint && 
                <div className="pg-tooltip"
                  style={{
                    left: `${hoveredPoint.cx + tooltipWidth > graphWidth ? hoveredPoint.cx - tooltipWidth - 12 : hoveredPoint.cx + 12}px`,
                    top: `${hoveredPoint.cy - 38}px`
                  }}
                >
                  <p className="pg-tooltip-title">{hoveredPoint.seasonName}</p>

                  <p>
                    {hoveredPoint.graphId
                      .split('_')
                      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
                      .join(' ')}
                    :{' '}
                    {Number.isInteger(hoveredPoint.value)
                      ? hoveredPoint.value
                      : hoveredPoint.value.toFixed(2)}
                  </p>
                </div>
              }
            </div>
            <div className='pg-category-container'>
              {
                Object.keys(visible).map((key) => {
                  const graphName = key.split('_').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ');
                  return (
                    <div key={key} onClick={() => toggleVisible(key)} className='pg-category' style={{color: colours[key]}}>
                      {
                        visible[key] 
                        ? <CheckBox />
                        : <CheckBoxOutlineBlank />
                      }
                      <p style={{color: colours[key]}}>{graphName}{graphName === 'Health' ? '*' : ''}</p>
                    </div>
                  )
                })
              }
            </div>
            <p className='pg-category-description'>
              * projects with at least 5 auditionees/role are assigned a health of 100. 
              Projects with at least 3 auditionees/role are assigned a health of 75. 
              The season's health is the average of its projects' health.
            </p>
          </div>
        : <SectionLoader height='400px' />
      }
    </>
  )

}



const getRoundedHighest = (graphs) => {
  const highest = Math.max(
    1,
    ...Object.values(graphs).flatMap(graph =>
      Object.values(graph.ys)
    )
  );

  let step;

  if (highest < 100) step = 10;
  else if (highest < 200) step = 25;
  else if (highest < 500) step = 50;
  else step = 100;

  return Math.ceil(highest / step) * step;
}


const getLabelLines = (label) => {
  // Autumn 2025 (First Half)
  const bracketIndex = label.indexOf(" (");
  if (bracketIndex !== -1) {
    return [
      label.slice(0, bracketIndex),
      label.slice(bracketIndex + 1) // "(First Half)"
    ];
  }

  // Spring 2025
  const yearMatch = label.match(/^(.*)\s(\d{4})$/);
  if (yearMatch) {
    return [yearMatch[1], yearMatch[2]];
  }

  // Fallback: split roughly in half
  const words = label.split(" ");
  const midpoint = Math.ceil(words.length / 2);
  return [
    words.slice(0, midpoint).join(" "),
    words.slice(midpoint).join(" ")
  ];
};